import dynamic from 'next/dynamic';
import { Text, Card, Box, Divider } from '@wix/design-system';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import { ArticleSearchResultItem, GenAiArticleLink } from '../../../../types';
import { DATA_HOOKS } from '../../../../dataHooks';
const GenAnswerLoader = dynamic(() => import('../GenAnswerLoader'), { ssr: false });
import { GenAnswerError } from './GenAnswerError';
import { RenderHTML } from '../../../RenderHTML';
import { InfoCircleSmall } from 'wix-ui-icons-common';
import { GenAnswerFooter } from './GenAnswerFooter';
import { countWords } from '../../../../utils/text';
import { formatRichText } from '..';
import css from './index.module.scss';

type GenAnswerBodyProps = {
  searchAnswer: string | null;
  searchQuestion: string;
  isProhibited: boolean | null;
  retryGenerateAnswer: () => Promise<void>;
  articleLinks: ArticleSearchResultItem[]
};


export const GenAnswerBody: FunctionComponent<GenAnswerBodyProps> = ({ searchQuestion, searchAnswer, isProhibited, retryGenerateAnswer, articleLinks }) => {
  const { t } = useTranslation();
  const [genAnswerRetries, setGenAnswerRetries] = useState(0);

  const genArticleLinks = (topFiveResults: ArticleSearchResultItem[]): GenAiArticleLink[] =>
    topFiveResults.map((result) => ({
      articleId: result.id,
      articleName: result.title,
      articleURL: result.url,
      distance: result.distance,
      docType: result.docType,
      type: result.type
    }));

  if (searchAnswer === null) {
    return (<Box dataHook={DATA_HOOKS.GEN_ANSWER_LOADER}>
      <GenAnswerLoader />
    </Box>);
  } else {
    if (isProhibited) {
      return (
        <Card className={css.answerProhibited}>
          <Text size='medium' dataHook={DATA_HOOKS.GEN_ANSWER_PROHIBITED} weight="thin">
            {t('search-results-page.gen-answer.prohibited')}
          </Text>
        </Card>
      );
    } else if (searchAnswer.length === 0) {
      return <GenAnswerError searchQuestion={searchQuestion} retryGenerateAnswer={retryGenerateAnswer} genAnswerRetries={genAnswerRetries} setGenAnswerRetries={setGenAnswerRetries} />;
    } else {
      return (
        <Card className={css.generatedAnswerCard}>
          <Text className={css.generatedAnswer} size='medium' dataHook={DATA_HOOKS.GEN_ANSWER_ANSWER} weight="thin">
            <RenderHTML html={formatRichText(searchAnswer)} />
          </Text>
          <Box direction="horizontal" className={css.infoWrapper}
            gap='3px' dataHook={DATA_HOOKS.GEN_ANSWER_LEGAL_INFO}>
            <InfoCircleSmall />
            <Text size='tiny' secondary >
              {t('search-results-page.gen-answer.legal-info')}
            </Text>
          </Box>
          {articleLinks.length > 0 &&
            <Divider className={css.horizontalDivider} />}
          <GenAnswerFooter
            moreInfoArticles={genArticleLinks(articleLinks)}
            question={searchQuestion}
            answerWordCount={countWords(searchAnswer)}
            searchAnswer={searchAnswer}
          />
        </Card>);
    }
  }
};
